import { useState, useEffect } from 'react';
import { ArrowLeft, Copy, Check, Key, Zap, Shield, BarChart3, Code2, Sparkles, Lock, ArrowRight, Database, FileCode } from 'lucide-react';

interface APIDocsPageProps {
  onBack: () => void;
}

function APIDocsPage({ onBack }: APIDocsPageProps) {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('quick-start');

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle hash navigation on initial load
  useEffect(() => {
    const hash = window.location.hash.slice(1); // Remove the # symbol
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        scrollToSection(hash);
      }, 100);
    }
  }, []);

  const navItems = [
    { id: 'quick-start', label: 'Quick Start', icon: Zap },
    { id: 'rate-limits', label: 'Rate Limits', icon: Shield },
    { id: 'conversion-apis', label: 'Conversion APIs', icon: Code2, isSection: true },
    { id: 'endpoints', label: 'API Endpoints', icon: FileCode, indent: true },
    { id: 'response-examples', label: 'Response Examples', icon: Check, indent: true },
    { id: 'mock-apis', label: 'Mock API Generator', icon: Database, isSection: true },
    { id: 'mock-getting-started', label: 'Getting Started', icon: Sparkles, indent: true },
    { id: 'mock-templates', label: 'Templates', icon: FileCode, indent: true },
    { id: 'mock-usage', label: 'Usage Examples', icon: Code2, indent: true },
  ];

  const CodeBlock = ({ code, language, id }: { code: string; language: string; id: string }) => (
    <div className="relative group">
      <div className="absolute right-2 top-2 z-10">
        <button
          onClick={() => copyToClipboard(code, id)}
          className="p-2 bg-white/90 hover:bg-white rounded-lg transition-all opacity-0 group-hover:opacity-100 border border-gray-200 shadow-sm backdrop-blur"
          title="Copy code"
        >
          {copiedCode === id ? (
            <Check className="w-4 h-4 text-[#4ECDC4]" />
          ) : (
            <Copy className="w-4 h-4 text-gray-600" />
          )}
        </button>
      </div>
      <pre className="bg-gradient-to-br from-gray-900 to-gray-800 text-gray-100 p-5 rounded-xl overflow-x-auto text-sm border border-gray-700 shadow-lg">
        <code className={`language-${language}`}>{code}</code>
      </pre>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFBFC]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between max-w-6xl mx-auto">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-[#1A535C] hover:text-[#FF6B6B] transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </button>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-lg flex items-center justify-center rotate-3 shadow-md">
                  <Code2 className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-[#1A535C] to-[#4ECDC4] bg-clip-text text-transparent">
                  API Documentation
                </h1>
              </div>
              <a
                href="/dashboard"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white rounded-lg font-medium hover:shadow-lg transition-all hover:scale-105 text-sm"
              >
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 pt-24 pb-12">
        <div className="max-w-7xl mx-auto flex gap-8">
          {/* Sidebar Navigation */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4 px-3">
                  Contents
                </h3>
                <nav className="space-y-1">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    const isSection = item.isSection;
                    const isIndented = item.indent;

                    return (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`
                          w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all
                          ${isIndented ? 'ml-6' : ''}
                          ${isSection ? 'font-bold text-gray-900 mt-3' : ''}
                          ${isActive && !isSection
                            ? 'bg-gradient-to-r from-[#FF6B6B]/10 to-[#4ECDC4]/10 text-[#1A535C] font-medium border-l-2 border-[#4ECDC4]'
                            : !isSection
                            ? 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            : 'text-gray-900'
                          }
                        `}
                      >
                        <Icon className={`w-4 h-4 ${isActive ? 'text-[#4ECDC4]' : ''}`} />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
        {/* Hero Section */}
        <div className="mb-16 text-center relative">
          {/* Floating elements */}
          <div className="absolute top-0 left-10 w-12 h-12 bg-[#FF6B6B]/10 rounded-2xl rotate-12 animate-float"></div>
          <div className="absolute top-10 right-10 w-16 h-16 bg-[#4ECDC4]/10 rounded-full animate-float-delayed"></div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF6B6B]/10 to-[#4ECDC4]/10 border border-[#4ECDC4]/20 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-[#4ECDC4]" />
            <span className="text-sm font-medium text-[#1A535C]">REST API v1.0</span>
          </div>

          <h2 className="text-5xl font-bold mb-4 bg-gradient-to-r from-gray-900 via-[#1A535C] to-[#4ECDC4] bg-clip-text text-transparent">
            JsonToonCraft REST API
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Integrate powerful JSON conversions into your applications with our fast, secure, and reliable API
          </p>
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white border border-gray-200 rounded-xl shadow-sm">
            <Code2 className="w-5 h-5 text-[#4ECDC4]" />
            <span className="text-[#1A535C] font-mono text-sm font-medium">https://jsontooncraft.com/api</span>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-16">
          <div className="bg-gradient-to-br from-white to-[#FFE5D9]/20 border border-[#FF6B6B]/20 rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B] to-[#ff8888] rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md">
              <Key className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900">Dashboard Keys</h3>
            <p className="text-sm text-gray-600">Manage keys via Google OAuth dashboard</p>
          </div>
          <div className="bg-gradient-to-br from-white to-[#E8F8F6]/50 border border-[#4ECDC4]/20 rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
            <div className="w-12 h-12 bg-gradient-to-br from-[#4ECDC4] to-[#6ed9d1] rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900">Lightning Fast</h3>
            <p className="text-sm text-gray-600">Serverless with edge caching</p>
          </div>
          <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
            <div className="w-12 h-12 bg-gradient-to-br from-[#1A535C] to-[#2d6a76] rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900">Rate Limited</h3>
            <p className="text-sm text-gray-600">Built-in abuse protection</p>
          </div>
          <div className="bg-gradient-to-br from-white to-[#FFE5D9]/20 border border-[#FF6B6B]/20 rounded-xl p-6 text-center hover:shadow-lg transition-all hover:-translate-y-1">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B] to-[#ff8888] rounded-xl flex items-center justify-center mx-auto mb-3 shadow-md">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <h3 className="font-semibold mb-2 text-gray-900">Usage Tracking</h3>
            <p className="text-sm text-gray-600">Monitor in dashboard</p>
          </div>
        </div>

        {/* Quick Start */}
        <section id="quick-start" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <div className="w-10 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-full"></div>
            Quick Start
          </h2>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-[#FFE5D9] to-[#E8F8F6] border-2 border-[#FF6B6B]/30 rounded-2xl p-8 shadow-lg">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-xl flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2 text-[#1A535C] flex items-center gap-2">
                    Get Your API Key
                    <Lock className="w-5 h-5 text-[#FF6B6B]" />
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Sign in to your dashboard with Google and create an API key (max 3 keys per account)
                  </p>
                  <a
                    href="/dashboard"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white rounded-lg font-medium hover:shadow-lg transition-all hover:scale-105"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#4ECDC4] to-[#6ed9d1] rounded-xl flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-4 text-[#1A535C]">Make Your First Request</h3>
                  <CodeBlock
                    id="first-request"
                    language="bash"
                    code={`curl -X POST https://jsontooncraft.com/api/convert/typescript \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "input": "{\\"name\\": \\"John\\", \\"age\\": 30}",
    "rootName": "User"
  }'`}
                  />
                  <div className="mt-4 p-4 bg-[#4ECDC4]/10 border border-[#4ECDC4]/30 rounded-lg">
                    <p className="text-sm text-[#1A535C]">
                      <strong>✨ Tip:</strong> Use <code className="px-2 py-1 bg-white/50 rounded font-mono text-xs">X-API-Key</code> or <code className="px-2 py-1 bg-white/50 rounded font-mono text-xs">Authorization: Bearer</code> header
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Rate Limits */}
        <section id="rate-limits" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <div className="w-10 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-full"></div>
            Rate Limits & Quotas
          </h2>

          <div className="bg-gradient-to-br from-white to-[#E8F8F6]/30 border border-[#4ECDC4]/30 rounded-2xl p-8 shadow-lg mb-6">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-[#4ECDC4] to-[#6ed9d1] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-[#1A535C]">Protected & Fair Usage</h3>
                <p className="text-gray-600">Our API implements rate limiting to ensure fair usage and protect against abuse. Choose an API tier that fits your needs.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* No API Key */}
              <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-gray-500" />
                  <h4 className="font-semibold text-gray-900">No API Key</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">10</span>
                    <span className="text-sm text-gray-600">requests/hour</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#FF6B6B]">3</span>
                    <span className="text-sm text-gray-600">requests/minute</span>
                  </div>
                  <p className="text-xs text-gray-500 pt-2">Per IP address. Perfect for testing the API.</p>
                </div>
              </div>

              {/* FREE Tier */}
              <div className="bg-gradient-to-br from-[#4ECDC4]/5 to-white border-2 border-[#4ECDC4] rounded-xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#4ECDC4]/10 rounded-full blur-2xl"></div>
                <div className="relative">
                  <div className="flex items-center gap-2 mb-4">
                    <Key className="w-5 h-5 text-[#4ECDC4]" />
                    <h4 className="font-semibold text-gray-900">FREE Tier</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-[#4ECDC4]">500</span>
                      <span className="text-sm text-gray-600">requests/hour</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900">10K</span>
                      <span className="text-sm text-gray-600">requests/month</span>
                    </div>
                    <p className="text-xs text-gray-500 pt-2">Get your free API key from the dashboard.</p>
                  </div>
                </div>
              </div>

              {/* PRO Tier */}
              <div className="bg-gradient-to-br from-[#FF6B6B]/5 to-white border-2 border-[#FF6B6B] rounded-xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 px-3 py-1 bg-[#FF6B6B] text-white text-xs font-semibold rounded-bl-lg">
                  COMING SOON
                </div>
                <div className="relative mt-2">
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="w-5 h-5 text-[#FF6B6B]" />
                    <h4 className="font-semibold text-gray-900">PRO Tier</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-[#FF6B6B]">5K</span>
                      <span className="text-sm text-gray-600">requests/hour</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900">100K</span>
                      <span className="text-sm text-gray-600">requests/month</span>
                    </div>
                    <p className="text-xs text-gray-500 pt-2">For production applications and high traffic.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payload Limits */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold mb-4 text-[#1A535C] flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#4ECDC4]" />
              Payload Limits
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#4ECDC4]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#4ECDC4] font-bold">📦</span>
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">Max Size</p>
                  <p className="text-2xl font-bold text-[#4ECDC4]">500KB</p>
                  <p className="text-xs text-gray-500 mt-1">Per request payload</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#FF6B6B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-[#FF6B6B] font-bold">🏗️</span>
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">Max Depth</p>
                  <p className="text-2xl font-bold text-[#FF6B6B]">10</p>
                  <p className="text-xs text-gray-500 mt-1">Nested object levels</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-600 font-bold">📊</span>
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">Max Array</p>
                  <p className="text-2xl font-bold text-purple-600">1,000</p>
                  <p className="text-xs text-gray-500 mt-1">Array elements</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rate Limit Headers */}
          <div className="mt-6 bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-3 text-[#1A535C]">Response Headers</h3>
            <p className="text-sm text-gray-600 mb-4">When you receive a 429 rate limit error, check these headers:</p>
            <div className="space-y-2 font-mono text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <ArrowRight className="w-4 h-4 text-[#4ECDC4]" />
                <code className="text-[#FF6B6B]">Retry-After</code>
                <span className="text-gray-500">- Seconds until you can retry</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700">
                <ArrowRight className="w-4 h-4 text-[#4ECDC4]" />
                <code className="text-[#FF6B6B]">X-RateLimit-Reset</code>
                <span className="text-gray-500">- Timestamp when limit resets</span>
              </div>
            </div>
          </div>
        </section>

        {/* Conversion APIs Section Header */}
        <div id="conversion-apis" className="scroll-mt-24 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Conversion APIs</h2>
          <p className="text-gray-600">Convert JSON to TypeScript, Zod, OpenAPI, and TOON format</p>
        </div>

        {/* Endpoints */}
        <section id="endpoints" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <div className="w-10 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-full"></div>
            API Endpoints
          </h2>

          <div className="space-y-6">
            {/* All Conversions - Featured */}
            <div className="bg-gradient-to-br from-[#FFE5D9] via-white to-[#E8F8F6] border-2 border-[#FF6B6B]/30 rounded-2xl p-8 shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#FF6B6B]/10 to-[#4ECDC4]/10 rounded-bl-full"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-4 py-2 bg-gradient-to-r from-[#4ECDC4] to-[#6ed9d1] text-white text-sm font-mono rounded-lg font-bold shadow-md">
                    POST
                  </span>
                  <code className="text-[#1A535C] font-mono font-semibold text-lg">/api/convert/all</code>
                  <span className="ml-auto px-4 py-2 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white text-xs font-bold rounded-lg shadow-md">
                    ⭐ RECOMMENDED
                  </span>
                </div>
                <p className="text-gray-900 mb-6 font-medium text-lg">
                  Get all conversions in a single request - TypeScript, Zod, OpenAPI & TOON!
                </p>

                <CodeBlock
                  id="all-curl"
                  language="bash"
                  code={`curl -X POST https://jsontooncraft.com/api/convert/all \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "input": "{\\"name\\": \\"John\\", \\"age\\": 30}",
    "rootName": "User"
  }'`}
                />
              </div>
            </div>

            {/* Individual Endpoints */}
            {[
              { path: '/api/convert/typescript', desc: 'Convert JSON to TypeScript interfaces' },
              { path: '/api/convert/zod', desc: 'Convert JSON to Zod validation schemas' },
              { path: '/api/convert/openapi', desc: 'Convert JSON to OpenAPI 3.1 schemas' },
              { path: '/api/convert/toon', desc: 'Convert JSON to TOON format (30-60% token savings)' },
              { path: '/api/convert/from-toon', desc: 'Convert TOON format back to JSON' },
            ].map((endpoint, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover:border-[#4ECDC4]/50">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className="px-3 py-1.5 bg-[#4ECDC4]/20 text-[#4ECDC4] text-sm font-mono rounded-lg border border-[#4ECDC4]/30 font-semibold">
                    POST
                  </span>
                  <code className="text-[#1A535C] font-mono font-medium">{endpoint.path}</code>
                </div>
                <p className="text-gray-700">{endpoint.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Mock API Generator Section */}
        <section id="mock-apis" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <div className="w-10 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-full"></div>
            Mock API Generator
          </h2>

          <div className="bg-gradient-to-br from-[#1A535C] to-[#14424a] text-white rounded-2xl p-8 shadow-xl mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#4ECDC4] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">Instant Mock REST APIs</h3>
                <p className="text-white/80">
                  Create fully functional mock REST APIs in seconds. Perfect for frontend development, prototyping, testing, and demos. No backend setup required!
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-[#4ECDC4] font-bold text-2xl mb-1">8</div>
                <div className="text-white/80 text-sm">Pre-built Templates</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-[#4ECDC4] font-bold text-2xl mb-1">10</div>
                <div className="text-white/80 text-sm">Endpoints per User</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-[#4ECDC4] font-bold text-2xl mb-1">500/hr</div>
                <div className="text-white/80 text-sm">Requests with API Key</div>
              </div>
            </div>
          </div>

          {/* Getting Started with Mock APIs */}
          <div id="mock-getting-started" className="bg-gradient-to-br from-[#FFE5D9] to-[#E8F8F6] border-2 border-[#FF6B6B]/30 rounded-2xl p-8 shadow-lg mb-8 scroll-mt-24">
            <h3 className="text-2xl font-bold mb-6 text-[#1A535C]">Getting Started</h3>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-xl flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                  1
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-2 text-gray-900">Sign in to Dashboard</h4>
                  <p className="text-gray-700 mb-3">Log in with Google OAuth and navigate to the "Mock APIs" tab</p>
                  <a
                    href="/dashboard"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white rounded-lg font-medium hover:shadow-lg transition-all hover:scale-105 text-sm"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Go to Dashboard
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-xl flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                  2
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-2 text-gray-900">Create Mock Endpoint</h4>
                  <p className="text-gray-700">Choose a pre-built template (users, movies, products, etc.) or paste your own custom JSON</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-xl flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                  3
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-2 text-gray-900">Get Your URL</h4>
                  <p className="text-gray-700">Instantly receive a live URL: <code className="bg-white px-2 py-1 rounded text-sm">https://jsontooncraft.com/api/mock/&#123;hash&#125;/&#123;endpoint&#125;</code></p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] rounded-xl flex items-center justify-center text-white font-bold shadow-md flex-shrink-0">
                  4
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold mb-2 text-gray-900">Start Making Requests</h4>
                  <p className="text-gray-700">Use it in your app immediately! No CORS issues, supports filtering, sorting, and pagination.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Available Templates */}
          <div id="mock-templates" className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg mb-8 scroll-mt-24">
            <h3 className="text-2xl font-bold mb-6 text-gray-900">Pre-built Templates</h3>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                { name: 'Users', desc: '100 user profiles with addresses, emails, and avatars', icon: '👤' },
                { name: 'Products', desc: '50 e-commerce products with prices, images, and ratings', icon: '🛍️' },
                { name: 'Movies', desc: '30 movies with genres, ratings, directors, and cast', icon: '🎬' },
                { name: 'Todos', desc: '20 task items with status and priority fields', icon: '✅' },
                { name: 'Posts', desc: '25 blog posts with authors, tags, and content', icon: '📝' },
                { name: 'Countries', desc: '50 countries with capitals, populations, and regions', icon: '🌍' },
                { name: 'Comments', desc: 'Nested comment threads for testing hierarchical data', icon: '💬' },
                { name: 'Complex Nested', desc: 'Multi-level organization structure for advanced testing', icon: '🏢' },
              ].map((template, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#4ECDC4] transition-all">
                  <span className="text-2xl">{template.icon}</span>
                  <div>
                    <h4 className="font-semibold text-gray-900">{template.name}</h4>
                    <p className="text-sm text-gray-600">{template.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Usage Examples */}
          <div id="mock-usage" className="space-y-6 scroll-mt-24">
            <h3 className="text-2xl font-bold text-gray-900">Usage Examples</h3>

            {/* Basic Request */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Basic Request (10 req/hour)</h4>
              <CodeBlock
                id="mock-basic"
                language="bash"
                code={`# Simple GET request
curl https://jsontooncraft.com/api/mock/abc123/movies`}
              />
            </div>

            {/* With API Key */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-3 text-gray-900">With API Key (500 req/hour, 10K/month)</h4>
              <CodeBlock
                id="mock-apikey"
                language="bash"
                code={`# Request with API key for higher limits
curl -H "X-API-Key: YOUR_API_KEY" \\
  https://jsontooncraft.com/api/mock/abc123/movies`}
              />
            </div>

            {/* Filtering */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Filtering Data</h4>
              <CodeBlock
                id="mock-filter"
                language="bash"
                code={`# Filter by field values
curl "https://jsontooncraft.com/api/mock/abc123/movies?genre=Action&year=2020"

# Full-text search
curl "https://jsontooncraft.com/api/mock/abc123/movies?q=inception"`}
              />
            </div>

            {/* Pagination */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Pagination & Sorting</h4>
              <CodeBlock
                id="mock-pagination"
                language="bash"
                code={`# Pagination (page 2, 10 items per page)
curl "https://jsontooncraft.com/api/mock/abc123/movies?_page=2&_limit=10"

# Sorting (by rating, descending)
curl "https://jsontooncraft.com/api/mock/abc123/movies?_sort=rating&_order=desc"`}
              />
            </div>

            {/* JavaScript Fetch */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-3 text-gray-900">JavaScript Example</h4>
              <CodeBlock
                id="mock-js"
                language="javascript"
                code={`// Fetch from your frontend (No CORS issues!)
fetch('https://jsontooncraft.com/api/mock/abc123/movies?_limit=10', {
  headers: {
    'X-API-Key': 'YOUR_API_KEY' // Optional, for higher limits
  }
})
  .then(res => res.json())
  .then(data => console.log(data));`}
              />
            </div>

            {/* React Example */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h4 className="text-lg font-semibold mb-3 text-gray-900">React Hook Example</h4>
              <CodeBlock
                id="mock-react"
                language="typescript"
                code={`import { useState, useEffect } from 'react';

function MovieList() {
  const [movies, setMovies] = useState([]);

  useEffect(() => {
    fetch('https://jsontooncraft.com/api/mock/abc123/movies?_limit=10')
      .then(res => res.json())
      .then(data => setMovies(data));
  }, []);

  return (
    <div>
      {movies.map(movie => (
        <div key={movie.id}>{movie.title}</div>
      ))}
    </div>
  );
}`}
              />
            </div>
          </div>

          {/* Query Parameters Reference */}
          <div className="bg-gradient-to-br from-white to-[#E8F8F6]/30 border border-[#4ECDC4]/30 rounded-2xl p-8 shadow-lg mt-8">
            <h3 className="text-2xl font-bold mb-6 text-[#1A535C]">Query Parameters Reference</h3>

            <div className="space-y-4">
              {[
                { param: 'field=value', desc: 'Filter by exact field match', example: '?userId=1&completed=true' },
                { param: 'q=text', desc: 'Full-text search across all fields', example: '?q=inception' },
                { param: '_page=N', desc: 'Page number (starts at 1)', example: '?_page=2' },
                { param: '_limit=N', desc: 'Items per page', example: '?_limit=10' },
                { param: '_sort=field', desc: 'Sort by field name', example: '?_sort=rating' },
                { param: '_order=asc|desc', desc: 'Sort order (ascending or descending)', example: '?_order=desc' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-white rounded-xl">
                  <code className="px-3 py-1 bg-[#4ECDC4]/10 text-[#4ECDC4] rounded-lg text-sm font-mono font-semibold flex-shrink-0">
                    {item.param}
                  </code>
                  <div className="flex-1">
                    <p className="text-gray-700 mb-1">{item.desc}</p>
                    <code className="text-xs text-gray-500 font-mono">{item.example}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rate Limits for Mock APIs */}
          <div className="bg-gradient-to-br from-[#FFE5D9] to-white border-2 border-[#FF6B6B]/30 rounded-2xl p-8 shadow-lg mt-8">
            <h3 className="text-2xl font-bold mb-6 text-[#1A535C]">Rate Limits</h3>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-5 h-5 text-gray-500" />
                  <h4 className="font-semibold text-gray-900">Without API Key</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">10</span>
                    <span className="text-sm text-gray-600">requests/hour</span>
                  </div>
                  <p className="text-sm text-gray-600">Per IP address • Perfect for testing and small projects</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-[#4ECDC4]/10 to-white rounded-xl p-6 border-2 border-[#4ECDC4]">
                <div className="flex items-center gap-2 mb-4">
                  <Key className="w-5 h-5 text-[#4ECDC4]" />
                  <h4 className="font-semibold text-gray-900">With API Key</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-[#4ECDC4]">500</span>
                    <span className="text-sm text-gray-600">requests/hour</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-[#4ECDC4]">10K</span>
                    <span className="text-sm text-gray-600">requests/month</span>
                  </div>
                  <p className="text-sm text-gray-600">Perfect for development and production</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Complete API Response Examples */}
        <section id="response-examples" className="mb-16 scroll-mt-24">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <div className="w-10 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-full"></div>
            Complete API Response Examples
          </h2>
          <p className="text-gray-600 mb-8">
            This section shows the <strong>exact response format</strong> you'll receive from each API endpoint.
          </p>

          <div className="space-y-8">
            {/* /api/convert/all Response */}
            <div className="bg-gradient-to-br from-[#E8F8F6] to-white border-2 border-[#4ECDC4]/30 rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold mb-4 text-[#1A535C] flex items-center gap-2">
                <span className="px-3 py-1 bg-[#4ECDC4] text-white text-sm rounded-lg font-mono">POST</span>
                /api/convert/all
              </h3>
              <p className="text-gray-700 mb-6">
                Returns all 4 formats in a single response: TypeScript, Zod, OpenAPI, and TOON
              </p>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Request:</h4>
                <CodeBlock
                  id="all-request"
                  language="bash"
                  code={`curl -X POST https://jsontooncraft.com/api/convert/all \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "input": "{\\"id\\":1,\\"name\\":\\"John Doe\\",\\"email\\":\\"john@example.com\\",\\"isActive\\":true}",
    "rootName": "User",
    "schemaName": "UserSchema"
  }'`}
                />
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Complete Response:</h4>
                <CodeBlock
                  id="all-response"
                  language="json"
                  code={`{
  "success": true,
  "output": {
    "typescript": "export interface User {\\n  id: number;\\n  name: string;\\n  email: string;\\n  isActive: boolean;\\n}",
    "zod": "import { z } from 'zod';\\n\\nexport const UserSchema = z.object({\\n  id: z.number(),\\n  name: z.string(),\\n  email: z.string(),\\n  isActive: z.boolean(),\\n});",
    "openapi": "{...}",
    "toon": "id: 1\\nname: John Doe\\nemail: john@example.com\\nisActive: true"
  }
}`}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-[#4ECDC4]/20">
                  <h5 className="font-semibold text-sm text-[#1A535C] mb-2">✅ TypeScript</h5>
                  <p className="text-xs text-gray-600">Frontend type safety</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-[#4ECDC4]/20">
                  <h5 className="font-semibold text-sm text-[#1A535C] mb-2">✅ Zod</h5>
                  <p className="text-xs text-gray-600">Runtime validation</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-[#4ECDC4]/20">
                  <h5 className="font-semibold text-sm text-[#1A535C] mb-2">✅ OpenAPI</h5>
                  <p className="text-xs text-gray-600">API documentation</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-[#4ECDC4]/20">
                  <h5 className="font-semibold text-sm text-[#1A535C] mb-2">✅ TOON</h5>
                  <p className="text-xs text-gray-600">30-60% fewer tokens</p>
                </div>
              </div>
            </div>

            {/* Individual Endpoint Responses */}
            {[
              {
                method: 'POST',
                path: '/api/convert/typescript',
                desc: 'TypeScript interfaces only',
                fieldType: 'string',
                response: `{
  "success": true,
  "output": "export interface User {\\n  id: number;\\n  name: string;\\n  email: string;\\n  isActive: boolean;\\n}",
  "format": "typescript"
}`,
              },
              {
                method: 'POST',
                path: '/api/convert/zod',
                desc: 'Zod validation schemas only',
                fieldType: 'string',
                response: `{
  "success": true,
  "output": "import { z } from 'zod';\\n\\nexport const UserSchema = z.object({\\n  id: z.number(),\\n  name: z.string(),\\n  email: z.string(),\\n  isActive: z.boolean(),\\n});",
  "format": "zod"
}`,
              },
              {
                method: 'POST',
                path: '/api/convert/openapi',
                desc: 'OpenAPI 3.1 schema only',
                fieldType: 'string',
                response: `{
  "success": true,
  "output": "{\\n  \\"openapi\\": \\"3.1.0\\",\\n  \\"info\\": {...},\\n  \\"components\\": {...}\\n}",
  "format": "openapi"
}`,
              },
              {
                method: 'POST',
                path: '/api/convert/toon',
                desc: 'TOON format only (30-60% token savings)',
                fieldType: 'string',
                response: `{
  "success": true,
  "output": "id: 1\\nname: John Doe\\nemail: john@example.com\\nisActive: true",
  "format": "toon"
}`,
              },
              {
                method: 'POST',
                path: '/api/convert/from-toon',
                desc: 'Convert TOON back to JSON',
                fieldType: 'string',
                response: `{
  "success": true,
  "output": "{\\"id\\":1,\\"name\\":\\"John Doe\\",\\"email\\":\\"john@example.com\\",\\"isActive\\":true}",
  "format": "json"
}`,
              },
            ].map((endpoint, idx) => (
              <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-xl font-bold mb-2 text-gray-900 flex items-center gap-2">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-lg font-mono">{endpoint.method}</span>
                  <code className="text-[#1A535C] font-mono">{endpoint.path}</code>
                </h3>
                <p className="text-gray-600 mb-4 text-sm">{endpoint.desc}</p>

                <div className="mb-3">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                    <span className="font-semibold text-blue-900">Response field:</span>
                    <code className="text-blue-700 font-mono">output ({endpoint.fieldType})</code>
                  </div>
                </div>

                <CodeBlock
                  id={`response-${idx}`}
                  language="json"
                  code={endpoint.response}
                />
              </div>
            ))}

            {/* Quick Comparison */}
            <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-bold mb-6 text-gray-900">Quick Comparison</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Endpoint</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Returns</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Response Field</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900">Best For</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">/typescript</td>
                      <td className="px-4 py-3">TypeScript only</td>
                      <td className="px-4 py-3 font-mono text-xs">output (string)</td>
                      <td className="px-4 py-3">Frontend types</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">/zod</td>
                      <td className="px-4 py-3">Zod only</td>
                      <td className="px-4 py-3 font-mono text-xs">output (string)</td>
                      <td className="px-4 py-3">Backend validation</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">/openapi</td>
                      <td className="px-4 py-3">OpenAPI only</td>
                      <td className="px-4 py-3 font-mono text-xs">output (string)</td>
                      <td className="px-4 py-3">API docs</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">/toon</td>
                      <td className="px-4 py-3">TOON only</td>
                      <td className="px-4 py-3 font-mono text-xs">output (string)</td>
                      <td className="px-4 py-3">LLM prompts</td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs">/from-toon</td>
                      <td className="px-4 py-3">JSON from TOON</td>
                      <td className="px-4 py-3 font-mono text-xs">output (string)</td>
                      <td className="px-4 py-3">TOON → JSON</td>
                    </tr>
                    <tr className="bg-[#E8F8F6]/50 hover:bg-[#E8F8F6]">
                      <td className="px-4 py-3 font-mono text-xs font-bold text-[#4ECDC4]">/all</td>
                      <td className="px-4 py-3 font-bold">All 4 formats</td>
                      <td className="px-4 py-3 font-mono text-xs font-bold text-[#4ECDC4]">output (object)</td>
                      <td className="px-4 py-3 font-bold text-[#4ECDC4]">Everything!</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-4 bg-[#4ECDC4]/10 border border-[#4ECDC4]/30 rounded-lg">
                <p className="text-sm text-[#1A535C]">
                  <strong>💡 Pro Tip:</strong> Use <code className="px-2 py-1 bg-white/50 rounded font-mono text-xs">/api/convert/all</code> to get all formats in one API call instead of making 4 separate requests!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Code Examples */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <div className="w-10 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-full"></div>
            Code Examples
          </h2>

          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-4 text-[#1A535C] flex items-center gap-2">
                <span className="text-2xl">🟨</span>
                JavaScript / Node.js
              </h3>
              <CodeBlock
                id="js-example"
                language="javascript"
                code={`const response = await fetch('https://jsontooncraft.com/api/convert/typescript', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY',
  },
  body: JSON.stringify({
    input: JSON.stringify({ name: 'John', age: 30 }),
    rootName: 'User',
  }),
});

const data = await response.json();
console.log(data.output);`}
              />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-semibold mb-4 text-[#1A535C] flex items-center gap-2">
                <span className="text-2xl">🐍</span>
                Python
              </h3>
              <CodeBlock
                id="python-example"
                language="python"
                code={`import requests
import json

response = requests.post(
    'https://jsontooncraft.com/api/convert/typescript',
    headers={
        'Content-Type': 'application/json',
        'X-API-Key': 'YOUR_API_KEY',
    },
    json={
        'input': json.dumps({'name': 'John', 'age': 30}),
        'rootName': 'User',
    }
)

print(response.json()['output'])`}
              />
            </div>
          </div>
        </section>

        {/* Error Handling */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3 text-gray-900">
            <div className="w-10 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] rounded-full"></div>
            Error Responses
          </h2>

          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-lg">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {[
                  { code: '400', desc: 'Bad Request - Invalid input or missing required fields' },
                  { code: '401', desc: 'Unauthorized - Invalid or missing API key' },
                  { code: '413', desc: 'Payload Too Large - Request exceeds 500KB limit' },
                  { code: '429', desc: 'Too Many Requests - Rate limit exceeded' },
                  { code: '500', desc: 'Internal Server Error - Server error during conversion' },
                ].map((error, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-[#FF6B6B]/10 text-[#FF6B6B] font-mono text-sm font-semibold rounded">
                        {error.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{error.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Support CTA */}
        <section className="mb-12">
          <div className="bg-gradient-to-br from-[#FFE5D9] via-white to-[#E8F8F6] border-2 border-[#FF6B6B]/20 rounded-3xl p-12 text-center shadow-xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#FF6B6B]/5 to-[#4ECDC4]/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#4ECDC4]/5 to-[#FF6B6B]/5 rounded-full blur-3xl"></div>

            <div className="relative">
              <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-gray-900 to-[#1A535C] bg-clip-text text-transparent">
                Need Help?
              </h2>
              <p className="text-gray-700 mb-8 text-lg max-w-2xl mx-auto">
                Check out our complete documentation or reach out for support
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a
                  href="/dashboard"
                  className="px-8 py-4 bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] text-white rounded-xl transition-all hover:scale-105 font-semibold shadow-lg flex items-center gap-2"
                >
                  <Key className="w-5 h-5" />
                  Get API Key
                </a>
              </div>
            </div>
          </div>
        </section>
          </main>
        </div>
      </div>
    </div>
  );
}

export default APIDocsPage;
